import { useQuery } from "@tanstack/react-query";
import { api } from "../api";

// --- Stream Event Types (discriminated union on `type`) ---

interface PriceChange {
  id: string;
  stayDate: string;
  roomTypePmsId: string;
  roomTypeName: string;
  previousPrice: number | null;
  newPrice: number;
  priceChange: number;
  priceChangePercent: number;
  narrative: string;
  trigger: string;
  floorApplied: boolean;
  ceilingApplied: boolean;
  classification: string | null;
  isSignificant: boolean;
  calculationDetail: Record<string, unknown>;
}

export interface PriceChangeRunEvent {
  type: "price_change_run";
  pushEventId: string;
  runAt: string;
  trigger: string;
  changeCount: number;
  significantCount: number;
  changes: PriceChange[];
}

export interface NoChangeRunEvent {
  type: "no_change_run";
  pushEventId: string;
  runAt: string;
  trigger: string;
  datesAnalyzed: number;
}

export interface SkippedRunEvent {
  type: "skipped_run";
  pushEventId: string;
  runAt: string;
  trigger: string;
  skipReason: string;
}

export interface RecommendationEvent {
  type: "recommendation";
  runAt: string;
  recommendation: {
    id: string;
    stayDate: string;
    roomTypePmsId: string;
    roomTypeName: string;
    type: "recommendation" | "free_upgrade";
    narrative: string;
    approvalStatus: "none" | "pending" | "approved" | "dismissed";
    constraintType: "floor" | "ceiling" | null;
    constraintValue: number | null;
    recommendedConstraintValue: number | null;
    affectedDates: string[] | null;
    createdAt: string;
  };
}

export interface ConfigChangeEvent {
  type: "config_change";
  runAt: string;
  configChange: {
    id: string;
    trigger: string;
    scope:
      | { type: "base" }
      | { type: "floor-ceiling"; startDate: string; endDate: string; roomTypes: string[] };
    before: Record<string, unknown>;
    after: Record<string, unknown>;
    reasoning: string | null;
    bodyMarkdown?: string;
  };
}

export interface RuleData {
  anchorCompetitor?: string;
  actions?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface RuleChangeEvent {
  type: "rule_change";
  runAt: string;
  ruleChange: {
    id: string;
    trigger: string;
    scope: {
      type: "rule";
      ruleId?: string;
      label?: string;
    };
    before: { rule?: RuleData; [key: string]: unknown };
    after: { rule?: RuleData; [key: string]: unknown };
    reasoning: string | null;
    bodyMarkdown?: string;
  };
}

export type StreamEvent =
  | PriceChangeRunEvent
  | NoChangeRunEvent
  | SkippedRunEvent
  | RecommendationEvent
  | ConfigChangeEvent
  | RuleChangeEvent;

export interface PriceStreamResponse {
  data: StreamEvent[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  dateFrom: string;
  dateTo: string;
}

// --- Query Keys ---

export const PRICING_STREAM_QUERY_KEY = "pricingStream";

// --- Hooks ---

export interface PricingStreamFilters {
  dateFrom?: string;
  dateTo?: string;
  eventType?: string;
  page?: number;
  limit?: number;
}

export function usePricingStream(filters?: PricingStreamFilters) {
  return useQuery<PriceStreamResponse>({
    queryKey: [PRICING_STREAM_QUERY_KEY, filters],
    queryFn: () => api.pricingNotifications.stream(filters) as Promise<PriceStreamResponse>,
    staleTime: 30 * 1000,
    refetchOnMount: "always",
  });
}
